import { useState } from "react";
import {
  MILESTONE_STEPS,
  MILESTONE_WINDOW_GAMES,
  MIN_PACE_GAMES,
  NEXT_ONE_WINDOW_GAMES,
  ordinal,
  RECORD_PACE_SHARE,
  STREAK_CHASE_GAP,
} from "@averagejoesff/shared";
import type {
  MilestoneCategory,
  MilestoneEntry,
  MilestoneKind,
  Milestones,
  MilestoneWhen,
  ReachedMilestone,
  RecordChase,
  RecordChaseKind,
} from "@averagejoesff/shared";
import { formatOdds, formatRecord } from "../../lib/format";
import type { CountingNote } from "./CountingNotes";
import { CountingNotes } from "./CountingNotes";
import type { PeopleLookup } from "./people";
import { ManagerLink, personName } from "./people";
import { RaceChart } from "./RaceChart";

interface MilestonesViewProps {
  milestones: Milestones;
  people: PeopleLookup;
  myId: string | null;
}

const KIND_TITLES: Record<MilestoneKind, string> = {
  careerWins: "Career wins",
  careerPoints: "Career points",
  hugeGames: "200-point games",
  bigGames: "150-point games",
  blowoutAwards: "Blowout awards",
  playoffAppearances: "Playoff appearances",
  playoffWins: "Playoff wins",
};

const CHASE_TITLES: Record<RecordChaseKind, string> = {
  seasonPointsPerGame: "Points per game in a season",
  seasonWins: "Wins in a regular season",
  seasonBigGames: "150-point games in a season",
  winStreak: "Longest win streak",
  careerWins: "Most career wins",
  careerPoints: "Most career points",
  careerBigGames: "Most 150-point games",
  careerHugeGames: "Most 200-point games",
  careerBlowoutAwards: "Most blowout awards",
};

/** The horizon's groups, in timeline order; "this week" has its own section above. */
const HORIZON: readonly { when: Exclude<MilestoneWhen, "thisWeek">; title: string }[] = [
  { when: "nextFewWeeks", title: "Next few weeks" },
  { when: "laterThisSeason", title: "Later this season" },
  { when: "ifPlayoffs", title: "If they make the playoffs" },
  { when: "nextSeason", title: "Next season" },
];

/** One milestone in sight, with the kind it belongs to. */
interface Moment {
  kind: MilestoneKind;
  entry: MilestoneEntry;
}

/** Whole numbers with separators; points drop their decimals, since the milestones are round thousands. */
const whole = (n: number) => Math.floor(n).toLocaleString("en-US");

/** "50th career win", "25,000 career points", "10th playoff trip". */
function milestoneName(kind: MilestoneKind, value: number): string {
  switch (kind) {
    case "careerWins":
      return `${ordinal(value)} career win`;
    case "careerPoints":
      return `${value.toLocaleString("en-US")} career points`;
    case "hugeGames":
      return `${ordinal(value)} 200-point game`;
    case "bigGames":
      return `${ordinal(value)} 150-point game`;
    case "blowoutAwards":
      return `${ordinal(value)} blowout award`;
    case "playoffAppearances":
      return `${ordinal(value)} playoff trip`;
    case "playoffWins":
      return `${ordinal(value)} playoff win`;
  }
}

/** What this week's game has to deliver: "Needs a win", "Needs 120 points". */
function needThisWeek({ kind, entry }: Moment): string {
  switch (kind) {
    case "careerWins":
      return "Needs a win";
    case "careerPoints":
      return `Needs ${Math.ceil(entry.needed).toLocaleString("en-US")} points`;
    case "hugeGames":
      return "Needs 200+ points";
    case "bigGames":
      return "Needs 150+ points";
    case "blowoutAwards":
      return "Needs the week's biggest win";
    case "playoffWins":
      return "Needs a playoff win";
    case "playoffAppearances":
      return "Needs a playoff spot";
  }
}

/** How far off a horizon moment is: "409 to go, about 3 games", "98% odds", "4 to go". */
function horizonDetail({ kind, entry }: Moment): string {
  if (entry.playoffOdds !== null) return `${formatOdds(entry.playoffOdds)} odds`;
  if (kind === "careerPoints") {
    const points = `${Math.ceil(entry.needed).toLocaleString("en-US")} to go`;
    return entry.gamesAway === null ? points : `${points}, about ${Math.max(1, Math.round(entry.gamesAway))} games`;
  }
  return entry.needed === 1 ? "the next one" : `${entry.needed} to go`;
}

/** Closest first: soonest by the estimate, then likeliest by the odds. */
const byCloseness = (a: Moment, b: Moment) =>
  (a.entry.gamesAway ?? Infinity) - (b.entry.gamesAway ?? Infinity) || (b.entry.playoffOdds ?? -1) - (a.entry.playoffOdds ?? -1);

/**
 * The milestones as moments on a timeline: what this week's games could
 * deliver, what's been reached this season, what's on the horizon (grouped
 * by when, not by stat), and the records within reach, raced against the
 * record season. Every number by category sits behind "Browse every milestone".
 */
export function MilestonesView({ milestones, people, myId }: MilestonesViewProps) {
  const [browsing, setBrowsing] = useState(false);
  const inSight: Moment[] = milestones.categories.flatMap((c) => c.entries.filter((e) => e.when !== null).map((entry) => ({ kind: c.kind, entry })));

  return (
    <div className="milestones">
      {milestones.offseason && <p className="home__line">The season's over, so these look ahead to next season.</p>}

      {!milestones.offseason && (
        <section>
          <h3 className="feature__subhead">On the line this week</h3>
          <ThisWeek moments={inSight.filter((m) => m.entry.when === "thisWeek")} opponents={milestones.opponents} people={people} myId={myId} />
        </section>
      )}

      <section>
        <h3 className="feature__subhead">{milestones.offseason ? `Reached in ${milestones.season}` : "Just reached this season"}</h3>
        <Reached reached={milestones.reached} people={people} />
      </section>

      <section>
        <h3 className="feature__subhead">On the horizon</h3>
        <Horizon moments={inSight.filter((m) => m.entry.when !== "thisWeek")} people={people} />
      </section>

      <section>
        <h3 className="feature__subhead">Record chase</h3>
        {milestones.recordChases.length === 0 ? (
          <p className="home__line">No records under threat right now.</p>
        ) : (
          <div className="milestones__group">
            {milestones.recordChases.map((chase) =>
              chase.race ? (
                <RaceCard key={`${chase.kind}-${chase.personId}`} chase={chase} people={people} />
              ) : (
                <ChaseCard key={`${chase.kind}-${chase.personId}`} chase={chase} people={people} myId={myId} />
              ),
            )}
          </div>
        )}
      </section>

      <section>
        <button type="button" className="milestones__toggle" aria-expanded={browsing} onClick={() => setBrowsing(!browsing)}>
          {browsing ? "Hide the full list" : "Browse every milestone"}
        </button>
        {browsing && (
          <div className="milestones__group">
            {milestones.categories
              .filter((category) => category.entries.length > 0)
              .map((category) => (
                <MilestoneCard key={category.kind} category={category} entries={category.entries} people={people} myId={myId} />
              ))}
          </div>
        )}
      </section>

      <CountingNotes id="milestones-notes-title" notes={HOW_COUNTED} />
    </div>
  );
}

interface ThisWeekProps {
  moments: readonly Moment[];
  opponents: Readonly<Record<string, string>>;
  people: PeopleLookup;
  myId: string | null;
}

/** One card per manager with something riding on this week's game, the viewer's first, then the busiest. */
function ThisWeek({ moments, opponents, people, myId }: ThisWeekProps) {
  if (moments.length === 0) return <p className="home__line">No milestones are riding on this week's games.</p>;
  const byManager = new Map<string, Moment[]>();
  for (const moment of moments) byManager.set(moment.entry.personId, [...(byManager.get(moment.entry.personId) ?? []), moment]);
  const cards = [...byManager].sort(([a, aMoments], [b, bMoments]) => Number(b === myId) - Number(a === myId) || bMoments.length - aMoments.length);

  return (
    <div className="moments">
      {cards.map(([personId, group]) => {
        const opponent = opponents[personId];
        return (
          <article key={personId} className={personId === myId ? "moment-card moment-card--mine" : "moment-card"}>
            <p className="moment-card__who">
              <ManagerLink personId={personId} people={people} />
              {opponent && <span className="moment-card__vs"> vs {personName(people, opponent)}</span>}
            </p>
            <ul className="moment-card__list">
              {group.map((moment) => (
                <li key={moment.kind} className="moment-card__moment">
                  <span className="moment-card__title">{milestoneName(moment.kind, moment.entry.target)}</span>
                  <span className="moment-card__need">{needThisWeek(moment)}</span>
                </li>
              ))}
            </ul>
          </article>
        );
      })}
    </div>
  );
}

/** Badges before "Show more": a full grid at every width (one, two or three across). */
const REACHED_SHOWN = 6;

/** A small mark for each kind, so a row of badges can be scanned by what was reached. */
const KIND_ICONS: Record<MilestoneKind, string> = {
  careerWins: "🏆",
  careerPoints: "🧮",
  hugeGames: "🔥",
  bigGames: "💪",
  blowoutAwards: "💥",
  playoffAppearances: "🎟️",
  playoffWins: "🥇",
};

/**
 * The season's reached milestones as a grid of equal badges, earliest place in
 * line first (shared's ordering): the milestone on top, then who, when and
 * "3rd ever" beneath, so every badge reads the same way and the ranking
 * explains itself.
 */
function Reached({ reached, people }: { reached: readonly ReachedMilestone[]; people: PeopleLookup }) {
  const [showAll, setShowAll] = useState(false);
  if (reached.length === 0) return <p className="home__line">None yet this season.</p>;
  const shown = showAll ? reached : reached.slice(0, REACHED_SHOWN);
  return (
    <>
      <ul className="reached">
        {shown.map((r) => (
          <li key={`${r.kind}-${r.personId}-${r.value}`} className="reached__badge">
            <span className="reached__icon" aria-hidden="true">
              {KIND_ICONS[r.kind]}
            </span>
            <span className="reached__text">
              <span className="reached__what">{milestoneName(r.kind, r.value)}</span>
              <span className="reached__who">
                <ManagerLink personId={r.personId} people={people} />
                <span className="reached__when">
                  {" "}
                  · Wk {r.week} · {ordinal(r.nth)} ever
                </span>
              </span>
            </span>
          </li>
        ))}
      </ul>
      {reached.length > REACHED_SHOWN && (
        <button type="button" className="reached__more" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
          {showAll ? "Show fewer" : `Show ${reached.length - REACHED_SHOWN} more`}
        </button>
      )}
    </>
  );
}

/** Everything else in sight, grouped by when rather than by stat. */
function Horizon({ moments, people }: { moments: readonly Moment[]; people: PeopleLookup }) {
  const groups = HORIZON.map((group) => ({ ...group, moments: moments.filter((m) => m.entry.when === group.when).sort(byCloseness) })).filter(
    (group) => group.moments.length > 0,
  );
  if (groups.length === 0) return <p className="home__line">Nothing else is in sight yet.</p>;
  return (
    <dl className="horizon">
      {groups.map((group) => (
        <div key={group.when} className="horizon__group">
          <dt className="horizon__when">{group.title}</dt>
          {group.moments.map((moment) => (
            <dd key={`${moment.kind}-${moment.entry.personId}`} className="horizon__moment">
              <span className="horizon__who">
                <ManagerLink personId={moment.entry.personId} people={people} />
              </span>{" "}
              <span>{milestoneName(moment.kind, moment.entry.target)}</span>
              <span className="horizon__detail"> · {horizonDetail(moment)}</span>
            </dd>
          ))}
        </div>
      ))}
    </dl>
  );
}

/** A season chase as a race: the chaser's running mark against the record season's, game by game. */
function RaceCard({ chase, people }: { chase: RecordChase; people: PeopleLookup }) {
  return (
    <article className="hof-card">
      <h4 className="hof-card__title">{CHASE_TITLES[chase.kind]}</h4>
      <div className="milestone__top">
        <ManagerLink personId={chase.personId} people={people} />
        <span className="milestone__count">{chaseMark(chase)}</span>
      </div>
      <RaceChart
        chaser={chase.race!.chaser}
        record={chase.race!.record}
        seasonGames={chase.race!.seasonGames}
        pace={chase.pace}
        chaserLabel={personName(people, chase.personId)}
        recordLabel={`${personName(people, chase.holderId)} ${chase.holderSeason}`}
        decimals={chase.kind === "seasonPointsPerGame" ? 1 : 0}
      />
      <p className="milestone__detail">{chaseDetail(chase, people)}</p>
    </article>
  );
}

interface MilestoneCardProps {
  category: MilestoneCategory;
  entries: readonly MilestoneEntry[];
  people: PeopleLookup;
  myId: string | null;
}

function MilestoneCard({ category, entries, people, myId }: MilestoneCardProps) {
  return (
    <article className="hof-card">
      <h4 className="hof-card__title">{KIND_TITLES[category.kind]}</h4>
      <ul className="milestone-list">
        {entries.map((entry) => (
          <ProgressRow
            key={entry.personId}
            personId={entry.personId}
            people={people}
            mine={entry.personId === myId}
            count={`${whole(entry.current)} / ${whole(entry.target)}`}
            progress={entry.progress}
            muted={!entry.soon}
            detail={milestoneDetail(category.kind, entry)}
          />
        ))}
      </ul>
    </article>
  );
}

/**
 * What the bar and count can't say: when it should come. "About 3 games",
 * "Week 9", "76% playoff odds", "Next season", or nothing.
 */
function milestoneDetail(kind: MilestoneKind, entry: MilestoneEntry): string {
  if (entry.nextSeason) return "Next season";
  if (entry.playoffOdds !== null) return `${formatOdds(entry.playoffOdds)} playoff odds`;
  if (kind === "careerPoints" && entry.gamesAway !== null) {
    return entry.gamesAway <= 1 ? "A typical game gets there" : `About ${Math.round(entry.gamesAway)} games`;
  }
  return "";
}

function ChaseCard({ chase, people, myId }: { chase: RecordChase; people: PeopleLookup; myId: string | null }) {
  return (
    <article className="hof-card">
      <h4 className="hof-card__title">{CHASE_TITLES[chase.kind]}</h4>
      <ul className="milestone-list">
        <ProgressRow
          personId={chase.personId}
          people={people}
          mine={chase.personId === myId}
          count={chaseMark(chase)}
          progress={chase.banked}
          projected={chase.progress}
          detail={chaseDetail(chase, people)}
        />
      </ul>
    </article>
  );
}

/** The number on the right: "5–1" for a wins chase, "4 in 6 games", "161.3 a game", "8 straight". */
function chaseMark(chase: RecordChase): string {
  const soFar = chase.seasonRecord;
  switch (chase.kind) {
    case "seasonWins":
      return soFar ? formatRecord(soFar.wins, soFar.losses, soFar.ties) : `${chase.current} wins`;
    case "seasonBigGames":
      return soFar ? `${chase.current} in ${soFar.wins + soFar.losses + soFar.ties} games` : String(chase.current);
    case "seasonPointsPerGame":
      return `${chase.current.toFixed(1)} a game`;
    case "careerWins":
      return `${chase.current} wins`;
    case "winStreak":
      return `${chase.current} straight`;
    case "careerPoints":
      return whole(chase.current);
    default:
      return String(chase.current);
  }
}

/**
 * "On pace for 11.7 wins · record 11, shared by 5, most recently Dillion in 2025",
 * "About 2 games to pass · record 109, Dillion".
 */
function chaseDetail(chase: RecordChase, people: PeopleLookup): string {
  const record = chase.kind === "seasonPointsPerGame" ? chase.record.toFixed(1) : chase.kind === "careerPoints" ? whole(chase.record) : String(chase.record);
  const holder = personName(people, chase.holderId);
  const when = chase.holderSeason === null ? "" : ` in ${chase.holderSeason}`;
  const set =
    chase.recordHolders <= 1
      ? `${holder}${when}`
      : chase.holderSeason === null
        ? `shared by ${chase.recordHolders}`
        : `shared by ${chase.recordHolders}, most recently ${holder}${when}`;
  const lead =
    chase.pace !== null
      ? `On pace for ${chase.pace.toFixed(1)}${chase.kind === "seasonWins" ? " wins" : ""}`
      : chase.gamesAway !== null
        ? `About ${Math.max(1, Math.round(chase.gamesAway))} ${Math.round(chase.gamesAway) <= 1 ? "game" : "games"} to pass`
        : "";
  // A live streak can already be past the old mark; it's still the one to watch.
  const passed = chase.pace === null && chase.current > chase.record;
  return [lead, `${passed ? "passed the record of" : "record"} ${record}, ${set}`].filter(Boolean).join(" · ");
}

interface ProgressRowProps {
  personId: string;
  people: PeopleLookup;
  mine: boolean;
  count: string;
  /** What's done, 0 to 1: the solid bar. */
  progress: number;
  /** Where a pace finishes, 0 to 1: a lighter bar behind the solid one, when it runs further. */
  projected?: number;
  /** Not close yet: the bar is grey instead of gold, so what's coming up stands out. */
  muted?: boolean;
  detail: string;
}

const percent = (share: number) => `${Math.round(Math.min(1, Math.max(0, share)) * 100)}%`;

/** Who, the number, a bar, and a muted line saying when; the bar is decoration, the text says it all. */
function ProgressRow({ personId, people, mine, count, progress, projected, muted = false, detail }: ProgressRowProps) {
  return (
    <li className={mine ? "milestone milestone--mine" : "milestone"}>
      <div className="milestone__top">
        <span className="milestone__who">
          <ManagerLink personId={personId} people={people} />
        </span>
        <span className="milestone__count">{count}</span>
      </div>
      <span className="milestone__bar" aria-hidden="true">
        {projected !== undefined && projected > progress && <span className="milestone__pace" style={{ width: percent(projected) }} />}
        <span className={muted ? "milestone__fill milestone__fill--muted" : "milestone__fill"} style={{ width: percent(progress) }} />
      </span>
      {detail && <p className="milestone__detail">{detail}</p>}
    </li>
  );
}

/** "Career wins every 25, career points every 5,000, ..." */
const INTERVALS = (Object.keys(KIND_TITLES) as MilestoneKind[])
  .map((kind, index) => `${index === 0 ? KIND_TITLES[kind] : KIND_TITLES[kind].toLowerCase()} every ${MILESTONE_STEPS[kind].toLocaleString("en-US")}`)
  .join(", ");

/** Each rule mirrors what shared's `buildMilestones` does; keep the two in step. */
const HOW_COUNTED: readonly CountingNote[] = [
  ["Counting", ["Regular-season and playoff games count; consolation games don't, as in the Hall of Fame."]],
  ["Milestones", [`${INTERVALS}.`]],
  [
    "When",
    [
      "On the line this week: a milestone this week's game alone could reach, like the next win, or points a typical game of theirs would cover.",
      `On the horizon: what the manager's career rate says should arrive within ${MILESTONE_WINDOW_GAMES} games, or the very next one of a count expected within about ${NEXT_ONE_WINDOW_GAMES} games. Points estimates use each manager's career average per game.`,
      "Just reached: every milestone crossed this season, with where the manager came in reaching it across league history (\"3rd ever\"; managers who got there the same week share a place). The earliest places come first, then the most recent.",
    ],
  ],
  ["Blowout awards", ["Each week's largest margin of victory, as on the Blowout page."]],
  [
    "Playoffs",
    [
      "A playoff appearance is a season with a playoff game; this season's counts once the field is set.",
      `During the regular season they show the playoff odds from Home. A playoff win comes into sight once the playoffs are within ${MILESTONE_WINDOW_GAMES} weeks.`,
    ],
  ],
  [
    "Record chase",
    [
      `A regular-season pace within ${Math.round((1 - RECORD_PACE_SHARE) * 100)}% of a single-season record, after ${MIN_PACE_GAMES} games. Season records come from finished seasons.`,
      "A season chase races the chaser's running total against the record season's, game by game; the dotted line is where the pace finishes.",
      `An active win streak within ${STREAK_CHASE_GAP} games of the longest ever.`,
      `A career total expected to pass the all-time leader within ${MILESTONE_WINDOW_GAMES} games.`,
    ],
  ],
  ["Browse", ["Every active manager's next milestone of each kind. A gold bar is in sight on the numbers; a grey one is further off."]],
];
